import { useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import FooterSection from '../home/components/FooterSection';
import EcommerceSection from './components/EcommerceSection';
import VitrineSection from './components/VitrineSection';
import OffresSummary from './components/OffresSummary';
import { Link } from 'react-router-dom';

export default function OffresPage() {
  useEffect(() => {
    // Canonical
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (canonical) {
      canonical.href = `${siteUrl}/offres`;
    } else {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      canonical.href = `${siteUrl}/offres`;
      document.head.appendChild(canonical);
    }

    // Title & meta description
    document.title = 'Offres & Tarifs Création Site Internet – UzunDigital';
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute(
      'content',
      'Découvrez les offres UzunDigital : site vitrine à partir de 69€/mois ou e-commerce dès 149€/mois. Livraison en 7 jours, sans engagement, hébergement inclus.',
    );

    // WebPage JSON-LD
    const pageScript = document.createElement('script');
    pageScript.type = 'application/ld+json';
    pageScript.id = 'ld-offres-page';
    pageScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Nos Offres & Tarifs – UzunDigital',
      url: `${siteUrl}/offres`,
      description: 'Offres de création de site internet professionnel : site vitrine à 69€/mois ou e-commerce à 149€/mois. Livraison en 7 jours, sans engagement.',
      inLanguage: 'fr-FR',
      isPartOf: { '@type': 'WebSite', url: siteUrl },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Accueil', item: `${siteUrl}/` },
          { '@type': 'ListItem', position: 2, name: 'Nos Offres', item: `${siteUrl}/offres` },
        ],
      },
    });

    // ItemList JSON-LD (pricing plans)
    const itemListScript = document.createElement('script');
    itemListScript.type = 'application/ld+json';
    itemListScript.id = 'ld-offres-list';
    itemListScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Offres de création de site internet – UzunDigital',
      url: `${siteUrl}/offres`,
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          item: {
            '@type': 'Service',
            name: 'Site Vitrine',
            description: 'Site vitrine professionnel sur mesure, responsive, SEO optimisé, hébergement inclus, livraison en 7 jours.',
            provider: { '@type': 'ProfessionalService', name: 'UzunDigital', url: siteUrl },
            offers: {
              '@type': 'Offer',
              price: '69',
              priceCurrency: 'EUR',
              priceSpecification: {
                '@type': 'RecurringPaymentFrequency',
                frequency: 'monthly',
              },
              description: 'Site vitrine à partir de 69€/mois sans engagement',
            },
          },
        },
        {
          '@type': 'ListItem',
          position: 2,
          item: {
            '@type': 'Service',
            name: 'Site E-commerce',
            description: 'Boutique en ligne complète avec paiement sécurisé, gestion commandes et stocks, SEO avancé, hébergement inclus.',
            provider: { '@type': 'ProfessionalService', name: 'UzunDigital', url: siteUrl },
            offers: {
              '@type': 'Offer',
              price: '149',
              priceCurrency: 'EUR',
              description: 'Site e-commerce à partir de 149€/mois sans engagement',
            },
          },
        },
      ],
    });

    document.head.appendChild(pageScript);
    document.head.appendChild(itemListScript);

    return () => {
      ['ld-offres-page', 'ld-offres-list'].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, []);

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <Navbar />

      {/* Hero header */}
      <section className="relative pt-28 sm:pt-36 pb-14 sm:pb-20 px-4 sm:px-6 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/b00f4ac4bdff727381906dbb328f96a3.jpg"
            alt="Background offres"
            className="w-full h-full object-cover object-top"
            loading="eager"
            width="1440"
            height="500"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/55 to-black/60"></div>
        </div>

        {/* Déco */}
        <div className="absolute top-10 right-10 w-[300px] h-[300px] bg-emerald-500/10 rounded-full blur-[100px]"></div>
        <div className="absolute bottom-10 left-10 w-[250px] h-[250px] bg-teal-500/10 rounded-full blur-[80px]"></div>

        <div className="relative z-10 max-w-4xl mx-auto text-center w-full">
          {/* Breadcrumb */}
          <div className="flex items-center justify-center space-x-2 mb-6 text-white/50 text-xs sm:text-sm font-semibold">
            <Link to="/" className="hover:text-emerald-400 transition-colors cursor-pointer">Accueil</Link>
            <i className="ri-arrow-right-s-line text-sm"></i>
            <span className="text-white/80">Nos Offres</span>
          </div>

          <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-md border border-white/20 text-white px-5 py-2.5 rounded-full text-xs sm:text-sm font-bold mb-7">
            <i className="ri-price-tag-3-line text-emerald-400 text-base"></i>
            <span>TARIFS TRANSPARENTS</span>
          </div>

          <h1 className="text-3xl sm:text-5xl md:text-6xl font-black text-white mb-5 leading-tight tracking-tight">
            Nos <span className="bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">Offres & Tarifs</span>
          </h1>
          <p className="text-base sm:text-xl text-white/70 max-w-2xl mx-auto font-semibold leading-relaxed mb-8">
            Sites Vitrine à partir de 69€/mois · E-commerce dès 149€/mois
          </p>

          {/* Badges réassurance */}
          <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-6 text-xs sm:text-sm text-white/60 font-semibold">
            <div className="flex items-center space-x-1.5">
              <i className="ri-timer-flash-line text-emerald-400 text-base"></i>
              <span>Livraison en 7 jours max</span>
            </div>
            <div className="flex items-center space-x-1.5">
              <i className="ri-check-line text-emerald-400 text-base"></i>
              <span>Sans engagement</span>
            </div>
            <div className="flex items-center space-x-1.5">
              <i className="ri-customer-service-2-line text-emerald-400 text-base"></i>
              <span>Support inclus</span>
            </div>
          </div>
        </div>
      </section>

      {/* Vitrine plans — EN PREMIER */}
      <VitrineSection />

      {/* Divider */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="border-t border-slate-100"></div>
      </div>

      {/* E-commerce plans */}
      <EcommerceSection />

      {/* Summary + CTA */}
      <OffresSummary />

      <FooterSection />
    </div>
  );
}
